import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Search, CalendarCheck, User, Store, Globe, Calculator, Sparkles, Ban } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";

export function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 font-extrabold text-xl" data-testid="logo-link" aria-label="Washly Startseite">
      <img src="/logo.png" alt="" className="h-8 w-auto" />
      <span>Washly</span>
    </Link>
  );
}

function useNavItems() {
  const { user } = useAuth();
  const { t } = useT();
  const items = [{ to: "/search", label: t("nav.search"), icon: Search, id: "search" }, { to: "/preise", label: t("nav.pricing"), icon: Calculator, id: "pricing" }];
  items.push({ to: "/waschheld", label: t("nav.hero"), short: "Waschheld", icon: Sparkles, id: "hero" });
  if (user) items.push({ to: "/bookings", label: t("nav.bookings"), icon: CalendarCheck, id: "bookings" });
  if (user?.role === "provider" || user?.has_provider) items.push({ to: "/provider", label: t("nav.provider"), icon: Store, id: "provider" });
  items.push({ to: user ? "/account" : "/auth", label: user ? t("nav.account") : t("nav.login"), icon: User, id: "account" });
  return items;
}

export function LangToggle() {
  const { lang, setLang } = useT();
  return (
    <button className="w-btn w-btn-secondary !px-4" onClick={() => setLang(lang === "de" ? "en" : "de")} data-testid="lang-toggle" aria-label="Sprache wechseln / switch language">
      <Globe size={18} aria-hidden="true" /> {lang === "de" ? "EN" : "DE"}
    </button>
  );
}

function TopBar() {
  const items = useNavItems();
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 w-glass">
      <div className="w-container flex h-16 items-center justify-between gap-4">
        <Logo />
        <nav className="hidden md:flex items-center gap-1" aria-label="Hauptnavigation">
          {items.map((i) => (
            <NavLink key={i.id} to={i.to} data-testid={`topnav-${i.id}`}
              className={({ isActive }) => `px-4 py-2 rounded-full font-semibold transition-colors ${isActive ? "bg-mint-soft text-mint-ink" : "hover:bg-gray-100"}`}>
              {i.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {!user && <button className="hidden md:inline-flex w-btn w-btn-secondary" onClick={() => nav("/auth?mode=register&role=provider")} data-testid="become-provider-btn">{t("nav.become")}</button>}
          <LangToggle />
        </div>
      </div>
      <div className="h-[3px] w-full w-accent-bar opacity-80" aria-hidden="true" />
    </header>
  );
}

function BottomNav() {
  const items = useNavItems();
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 h-16 bg-white border-t border-line grid" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }} aria-label="Navigation">
      {items.map(({ to, label, short, icon: Icon, id }) => (
        <NavLink key={id} to={to} data-testid={`bottomnav-${id}`}
          className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 text-sm font-semibold ${isActive ? "text-mint-ink" : "text-ink-2"}`}>
          {({ isActive }) => (<><Icon size={22} aria-hidden="true" strokeWidth={isActive ? 2.6 : 2} /><span className="text-xs">{short || label}</span></>)}
        </NavLink>
      ))}
    </nav>
  );
}

function BlockBanner() {
  const { user } = useAuth();
  const nav = useNavigate();
  if (!user?.blocked) return null;
  return (
    <div className="bg-[var(--w-error-soft)] border-b border-[var(--w-error)]/30" data-testid="block-banner">
      <div className="w-container py-3 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 font-semibold text-[var(--w-error)]"><Ban size={18} aria-hidden="true" /> Dein Konto ist aktuell gesperrt.</span>
        <span className="w-muted text-sm flex-1 min-w-[200px]">{user.block_reason || "Buchungen sind vorübergehend nicht möglich."} Für Rückfragen schreib uns im Postfach.</span>
        <button className="w-btn w-btn-secondary !min-h-[38px] !px-4" onClick={() => nav("/account")} data-testid="block-banner-contact">Zum Postfach</button>
      </div>
    </div>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  const [live, setLive] = useState(null);
  useEffect(() => { api.get("/site-status").then(({ data }) => setLive(!!data.payments_live)).catch(() => setLive(null)); }, []);
  const links = [["/impressum", "Impressum"], ["/datenschutz", "Datenschutz"], ["/agb", "AGB"], ["/widerruf", "Widerruf"], ["/widerruf-erklaeren", "Vertrag widerrufen"], ["/blog", "Journal"]];
  return (
    <footer className="relative overflow-hidden mt-16 text-white" data-testid="site-footer" style={{ background: "linear-gradient(135deg, #022c22 0%, #04231f 45%, #1c1033 100%)" }}>
      <div className="h-[3px] w-full w-accent-bar" aria-hidden="true" />
      <div className="w-blob w-blob-c" style={{ top: "-170px", left: "-80px" }} aria-hidden="true" />
      <div className="w-blob w-blob-violet" style={{ bottom: "-190px", right: "-60px" }} aria-hidden="true" />
      <div className="relative w-container py-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
        <div className="flex items-center gap-2"><Logo /></div>
        <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
          {links.map(([to, label]) => (
            <Link key={to} to={to} className="text-white/70 hover:text-white transition-colors" data-testid={`footer-link-${to.slice(1)}`}>{label}</Link>
          ))}
        </nav>
        <p className="text-white/60 text-sm">© {year} Washly{live === false ? " · Testmodus – keine echten Zahlungen" : ""}</p>
      </div>
    </footer>
  );
}


export default function CustomerShell() {
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-white p-2 z-50">Zum Inhalt springen</a>
      <TopBar />
      <BlockBanner />
      <main id="main"><Outlet /></main>
      <Footer />
      <BottomNav />
    </div>
  );
}
